import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Copy, Check, ChevronDown, ChevronUp, Save, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fixUrl = (url) => {
  if (!url || url.startsWith('data:')) return url;
  if (url.includes('emergentagent.com/api/') || url.includes('getmealguide.com/api/')) {
    const m = url.match(/\/api\/uploads\/.+$/);
    return m ? `${BACKEND_URL}${m[0]}` : url;
  }
  return url;
};

const API = `${BACKEND_URL}/api`;

// ── Macro helpers (handles both flat and nested schema) ───────────────────────
const kcal    = (r) => r.totals?.calories ?? r.total_kcal    ?? 0;
const protein = (r) => r.totals?.protein  ?? r.total_protein ?? 0;
const carbs   = (r) => r.totals?.carbs    ?? r.total_carbs   ?? 0;
const fat     = (r) => r.totals?.fat      ?? r.total_fat     ?? 0;

// ── Meal type label ────────────────────────────────────────────────────────────
const mealLabel = (recipe) => {
  const mt = (recipe.meal_type || '').toLowerCase().replace(/_/g, ' ');
  if (mt === 'breakfast') return 'Breakfast';
  if (['lunch', 'dinner', 'main meal'].includes(mt)) return 'Main Meal';
  if (mt === 'snack') return 'Snack';
  return recipe.meal_type || 'Recipe';
};

// ── Tier kcal targets ─────────────────────────────────────────────────────────
const tierKcal = (recipe) =>
  (recipe.meal_type || '').toLowerCase() === 'snack' ? 300 : 500;

// ── Scale ratio ───────────────────────────────────────────────────────────────
const ratio = (recipe) => {
  const k = kcal(recipe);
  return k > 0 ? tierKcal(recipe) / k : 1;
};

// ── Scale numeric values inside a text string ─────────────────────────────────
const scaleText = (text, r) => {
  if (!text || r === 1) return text;
  let out = text.replace(/(\d+\.?\d*)\s*(g|ml|kg|l)\b/gi,
    (_, n, u) => `${Math.round(parseFloat(n) * r)}${u}`);
  out = out.replace(/^(\d+)\s+/, (_, n) => `${Math.round(parseInt(n, 10) * r)} `);
  return out;
};

const ingName = (i) =>
  i.food_bank_name || i.food_item_name || i.ingredient_name || i.name || '';

// ── Build scaled ingredient list ──────────────────────────────────────────────
const buildIngList = (recipe, r) => {
  const cookingEssentials = Array.isArray(recipe.cooking_essentials) ? recipe.cooking_essentials : [];
  const essentialIds = new Set(cookingEssentials.map(e => e.food_bank_id));
  return (recipe.ingredients || [])
    .filter(i => !essentialIds.has(i.food_bank_id || i.id))
    .map(i => {
      const name = ingName(i);
      const qty = i.display_text ||
        `${i.quantity || i.amount || i.grams || ''} ${i.unit || (i.grams ? 'g' : '')}`.trim();
      return `- ${scaleText(qty, r)}${name ? ' ' + name : ''}`;
    })
    .join('\n');
};

// ── Format instructions — ensure each numbered step is on its own line with a blank line gap
const formatInstructions = (text) => {
  if (!text) return '';
  // If steps are crammed on one line (e.g. "1. Do this 2. Do that"), split them out
  return text
    .replace(/(\d+\.\s)/g, '\n$1')   // newline before each "1. "
    .replace(/\n{3,}/g, '\n\n')       // collapse triple+ newlines to double
    .trim();
};

// ── Fill template with recipe data ────────────────────────────────────────────
const fillTemplate = (tpl, recipe) => {
  const r = ratio(recipe);
  return tpl
    .replace(/{{recipe_name}}/g, recipe.name || '')
    .replace(/{{kcal}}/g,        String(Math.round(kcal(recipe)    * r)))
    .replace(/{{protein}}/g,     String(Math.round(protein(recipe) * r)))
    .replace(/{{carbs}}/g,       String(Math.round(carbs(recipe)   * r)))
    .replace(/{{fat}}/g,         String(Math.round(fat(recipe)     * r)))
    .replace(/{{ingredients}}/g, buildIngList(recipe, r))
    .replace(/{{instructions}}/g, formatInstructions(recipe.instructions))
    .replace(/{{meal_type}}/g,   mealLabel(recipe));
};

// ── Default template (shown if coach hasn't saved one yet) ────────────────────
const DEFAULT_TPL =
`{{recipe_name}} 🍽

📊 Macros ({{kcal}} kcal serving)
Protein: {{protein}}g | Carbs: {{carbs}}g | Fat: {{fat}}g

🛒 Ingredients:
{{ingredients}}

👨‍🍳 Method:
{{instructions}}

Get your personalised plan 👉 getmealguide.com`;

const FILTERS = ['All', 'Breakfast', 'Main Meal', 'Snack'];

// ── Component ─────────────────────────────────────────────────────────────────
export default function ContentCreator({ recipes = [] }) {
  const [mealFilter,      setMealFilter]      = useState('All');
  const [template,        setTemplate]        = useState(DEFAULT_TPL);
  const [savedTemplate,   setSavedTemplate]   = useState(DEFAULT_TPL);
  const [tplOpen,         setTplOpen]         = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [loading,         setLoading]         = useState(true);
  const [selected,        setSelected]        = useState(null);
  const [copiedSnippet,   setCopiedSnippet]   = useState(false);
  const [copiedCaption,   setCopiedCaption]   = useState(false);

  // Load saved template from DB on mount
  useEffect(() => {
    axios.get(`${API}/coach/caption-template`)
      .then(r => { setTemplate(r.data.template); setSavedTemplate(r.data.template); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/coach/caption-template`, { template });
      setSavedTemplate(template);
      toast.success('Template saved');
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Normalise id field (tier-recipes uses recipe_id, library-recipes uses id)
  const normalised = recipes.map(r => ({ ...r, id: r.id || r.recipe_id }));

  const filtered = normalised.filter(r =>
    mealFilter === 'All' || mealLabel(r) === mealFilter
  );

  const copy = async (text, setter) => {
    await navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const snippetText = (r) => {
    const rv = ratio(r);
    return `${r.name} — ${Math.round(kcal(r) * rv)} kcal · ${Math.round(protein(r) * rv)}g protein`;
  };

  const dirty = template !== savedTemplate;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-[#1a61e8]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Template editor — collapsible */}
      <div className="border rounded-xl overflow-hidden">
        <button
          onClick={() => setTplOpen(o => !o)}
          data-testid="toggle-template-editor"
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
        >
          <span>
            Caption Template
            {dirty && (
              <span className="ml-2 text-xs text-amber-500 font-normal">unsaved changes</span>
            )}
          </span>
          {tplOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {tplOpen && (
          <div className="p-4 space-y-3 bg-white">
            <p className="text-xs text-gray-400 leading-relaxed">
              Placeholders auto-filled from each recipe (scaled to 500 kcal for meals, 300 kcal for snacks):&nbsp;
              {['recipe_name','kcal','protein','carbs','fat','ingredients','instructions','meal_type'].map(p => (
                <code key={p} className="bg-gray-100 text-gray-600 px-1 py-0.5 rounded text-[11px] mr-1">
                  {`{{${p}}}`}
                </code>
              ))}
            </p>
            <Textarea
              value={template}
              onChange={e => setTemplate(e.target.value)}
              className="font-mono text-sm min-h-[240px] resize-y"
              data-testid="caption-template-input"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline" size="sm"
                onClick={() => setTemplate(savedTemplate)}
                disabled={!dirty}
              >
                Discard
              </Button>
              <Button
                size="sm"
                onClick={saveTemplate}
                disabled={!dirty || saving}
                className="bg-[#1a61e8] hover:bg-[#1a61e8]/90"
                data-testid="save-template-btn"
              >
                {saving
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <Save className="h-3.5 w-3.5 mr-1.5" />
                }
                Save template
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Meal type filter pills */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => { setMealFilter(f); setSelected(null); }}
            data-testid={`filter-${f.toLowerCase().replace(' ', '-')}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              mealFilter === f
                ? 'bg-[#1a61e8] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f}
            {f !== 'All' && (
              <span className="ml-1 opacity-60">
                ({normalised.filter(r => mealLabel(r) === f).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

        {/* Recipe list */}
        <div className="space-y-1.5 max-h-[620px] overflow-y-auto pr-1">
          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-12">No recipes found.</p>
          )}
          {filtered.map(recipe => {
            const rv      = ratio(recipe);

            const isSelected = selected?.id === recipe.id;
            return (
              <button
                key={recipe.id}
                onClick={() => setSelected(isSelected ? null : recipe)}
                data-testid={`recipe-row-${recipe.id}`}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-[#1a61e8] bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {/* Thumbnail */}
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                  {recipe.image_url
                    ? <img src={fixUrl(recipe.image_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    : <div className="w-full h-full bg-gray-200" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-900 truncate">{recipe.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {Math.round(kcal(recipe) * rv)} kcal · {Math.round(protein(recipe) * rv)}g protein
                    <span className="ml-1 text-gray-300">({tierKcal(recipe)} kcal tier)</span>
                  </div>
                </div>
                <Badge variant="secondary" className="text-xs flex-shrink-0">
                  {mealLabel(recipe)}
                </Badge>
              </button>
            );
          })}
        </div>

        {/* Caption panel */}
        <div className="sticky top-4">
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 text-sm">
              Select a recipe to generate its caption
            </div>
          ) : (
            <div className="border rounded-xl overflow-hidden bg-white">
              {/* Recipe image — full width, right-click to copy URL */}
              {selected.image_url && (
                <img
                  src={fixUrl(selected.image_url)}
                  alt={selected.name}
                  className="w-full h-48 object-cover"
                  title="Right-click → Copy image address"
                />
              )}
              {/* Header */}
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-sm text-gray-900 truncate">{selected.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {mealLabel(selected)} · {tierKcal(selected)} kcal tier
                  </div>
                </div>
                <Badge variant="secondary" className="text-xs flex-shrink-0">{mealLabel(selected)}</Badge>
              </div>

              <div className="p-4 space-y-4">
                {/* Quick-copy snippet */}
                <div>
                  <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                    Quick copy
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-50 border rounded-lg px-3 py-2 text-sm text-gray-700 truncate">
                      {snippetText(selected)}
                    </code>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => copy(snippetText(selected), setCopiedSnippet)}
                      data-testid="copy-snippet-btn"
                      className="flex-shrink-0 w-9 h-9 p-0"
                    >
                      {copiedSnippet
                        ? <Check className="h-3.5 w-3.5 text-green-500" />
                        : <Copy className="h-3.5 w-3.5" />
                      }
                    </Button>
                  </div>
                </div>

                {/* Full caption */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                      Caption
                    </div>
                    <Button
                      size="sm"
                      onClick={() => copy(fillTemplate(template, selected), setCopiedCaption)}
                      data-testid="copy-caption-btn"
                      className="h-7 text-xs px-2.5 bg-[#1a61e8] hover:bg-[#1a61e8]/90"
                    >
                      {copiedCaption
                        ? <><Check className="h-3 w-3 mr-1" /> Copied</>
                        : <><Copy className="h-3 w-3 mr-1" /> Copy</>
                      }
                    </Button>
                  </div>
                  <pre className="bg-gray-50 border rounded-lg px-3 py-3 text-sm text-gray-700 whitespace-pre-wrap font-sans max-h-[320px] overflow-y-auto leading-relaxed">
                    {fillTemplate(template, selected)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
